// src/components/forms/teamForms/editAddress.js
import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import TextInput from '../formComponents/TextInput';
import SubmitButton from '../formComponents/SubmitButton';
import ErrorMessage from '../formComponents/ErrorMessage';
import CountryPicker from '../formComponents/CountryPicker';
import TeamMap from '../../teams/TeamMap';
import '../../../index.css';
import { geocodeAddress as geocodeUtil } from '../../../utils/geocoding';
import { normalizeAddress } from '../../teams/utils/normalizeAddress';
const MIN_QUERY = 3;
const SUGGEST_LIMIT = 5;
const DEBOUNCE_MS = 350;
function extractFromNominatim(item) {
  const a = item.address || {};
  const street = [a.house_number, a.road || a.pedestrian || a.footway || a.path || a.cycleway].filter(Boolean).join(' ').trim();
  const city = a.city || a.town || a.village || a.suburb || a.hamlet || '';
  const state = a.county || a.state_district || a.state || '';
  const postcode = a.postcode || '';
  const country = a.country || '';
  const lat = parseFloat(item.lat);
  const lng = parseFloat(item.lon);
  return {
    street,
    city,
    state,
    postcode,
    country,
    geo: {
      lat,
      lng
    }
  };
}
export default function EditAddressForm({
  initialAddress = {},
  isOpen,
  onClose,
  onSave
}) {
  const normalizedInitialAddress = normalizeAddress(initialAddress);

  // ---------- state ----------
  const [street, setStreet] = useState(normalizedInitialAddress.street || '');
  const [city, setCity] = useState(normalizedInitialAddress.city || '');
  const [stateProvince, setStateProvince] = useState(normalizedInitialAddress.state || '');
  const [postcode, setPostcode] = useState(normalizedInitialAddress.postcode || '');
  const [country, setCountry] = useState(normalizedInitialAddress.country || '');
  const [geo, setGeo] = useState(normalizedInitialAddress.geo || null);
  const [search, setSearch] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [highlight, setHighlight] = useState(-1);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [searchMode, setSearchMode] = useState(!normalizedInitialAddress.street && !normalizedInitialAddress.city && !normalizedInitialAddress.postcode && !normalizedInitialAddress.country);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const abortRef = useRef(null);
  const searchInputRef = useRef(null);
  const debouncedValue = useDebounce(search, DEBOUNCE_MS);
  const initialGeoLabelRef = useRef(normalizedInitialAddress.geoLabel || '');

  useEffect(() => {
    const nextAddress = normalizeAddress(initialAddress);
    const hasAddress = [nextAddress.street, nextAddress.city, nextAddress.state, nextAddress.postcode, nextAddress.country].some(Boolean);

    setStreet(nextAddress.street || '');
    setCity(nextAddress.city || '');
    setStateProvince(nextAddress.state || '');
    setPostcode(nextAddress.postcode || '');
    setCountry(nextAddress.country || '');
    setGeo(nextAddress.geo || null);
    initialGeoLabelRef.current = nextAddress.geoLabel || '';
    setSearch('');
    setSuggestions([]);
    setHighlight(-1);
    setOpen(false);
    setSearchMode(!hasAddress);
    setError(null);
  }, [initialAddress]);

  useEffect(() => {
    if (!isOpen || !searchMode) return;
    const id = setTimeout(() => {
      searchInputRef.current?.focus();
    }, 0);
    return () => clearTimeout(id);
  }, [isOpen, searchMode]);

  // ---------- fetch suggestions (only in search mode) ----------
  useEffect(() => {
    if (!isOpen || !searchMode) return;
    const q = debouncedValue?.trim();
    if (!q || q.length < MIN_QUERY) {
      setSuggestions([]);
      setOpen(false);
      return;
    }
    const params = new URLSearchParams({
      q,
      format: 'json',
      addressdetails: '1',
      limit: String(SUGGEST_LIMIT),
      dedupe: '1'
    });
    if ((country || '').toLowerCase() === 'united kingdom') {
      params.set('countrycodes', 'gb');
    }
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setLoading(true);
    fetch(`https://nominatim.openstreetmap.org/search?${params.toString()}`, {
      signal: controller.signal,
      headers: {
        'Accept-Language': 'en-GB,en;q=0.9'
      }
    }).then(res => res.json()).then(data => {
      if (!Array.isArray(data)) throw new Error('Invalid response');
      setSuggestions(data);
      setOpen(true);
      setHighlight(-1);
    }).catch(err => {
      if (err.name !== 'AbortError') {
        setSuggestions([]);
        setOpen(false);
      }
    }).finally(() => setLoading(false));
    return () => controller.abort();
  }, [debouncedValue, country, isOpen, searchMode]);

  // ---------- select a suggestion ----------
  const selectSuggestion = item => {
    const {
      street,
      city,
      state,
      postcode,
      country,
      geo
    } = extractFromNominatim(item);
    setStreet(street);
    setCity(city);
    setStateProvince(state);
    setPostcode(postcode);
    setCountry(country);
    setGeo(geo);
    setSearch(item.display_name || '');
    setOpen(false);
    setSuggestions([]);
    setSearchMode(false);
  };

  // ---------- keyboard nav ----------
  const handleKeyDown = e => {
    if (!open || suggestions.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight(h => Math.min(h + 1, suggestions.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight(h => Math.max(h - 1, 0));
    } else if (e.key === 'Enter') {
      if (highlight >= 0 && highlight < suggestions.length) {
        e.preventDefault();
        selectSuggestion(suggestions[highlight]);
      }
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  const draftAddress = normalizeAddress({
    street,
    city,
    state: stateProvince,
    postcode,
    country,
    geo,
    geoLabel: [street, city, stateProvince, postcode, country].filter(Boolean).join(', ')
  });

  // ---------- submit ----------
  const handleSubmit = async e => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    const updated = {
      street,
      city,
      state: stateProvince,
      postcode,
      country
    };
    try {
      const fullAddress = [street, city, stateProvince, postcode, country].filter(Boolean).join(', ');
      const shouldRefreshGeo = !geo || initialGeoLabelRef.current !== fullAddress;
      const finalGeo = shouldRefreshGeo && fullAddress ? await geocodeUtil({
        street,
        city,
        stateProvince,
        postcode,
        country
      }) : geo;
      await onSave({
        ...updated,
        geo: finalGeo || null,
        geoLabel: fullAddress
      });
    } catch (err) {
      setError(err?.message || 'Failed to update address');
    } finally {
      setSubmitting(false);
    }
  };
  if (!isOpen) return null;

  // ---------- UI ----------
  return <>
      <h2 className="modal-title">Edit Address</h2>
      {error && <ErrorMessage message={error} />}

      <form onSubmit={handleSubmit} className="modal-form" autoComplete="off">
        {searchMode ? <div className="form-group" style={{
        position: 'relative'
      }}>
            <TextInput id="addressSearch" label="Find your address" ref={searchInputRef} value={search} onChange={e => setSearch(e.target.value)} onKeyDown={handleKeyDown} onFocus={() => suggestions.length && setOpen(true)} disabled={submitting} placeholder="Start typing address or postcode…" />
            {open && suggestions.length > 0 && <ul className="autocomplete-list" style={{
          position: 'absolute',
          zIndex: 20,
          top: '100%',
          left: 0,
          right: 0,
          background: '#fff',
          border: '1px solid #e5e7eb',
          borderTop: 'none',
          maxHeight: 240,
          overflowY: 'auto',
          boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
          margin: 0,
          padding: 0,
          listStyle: 'none'
        }}>
              {suggestions.map((s, i) => <li key={`${s.place_id}-${i}`} onMouseDown={e => {
            e.preventDefault();
            selectSuggestion(s);
          }} onMouseEnter={() => setHighlight(i)} style={{
            padding: '10px 12px',
            cursor: 'pointer',
            background: i === highlight ? '#f3f4f6' : 'white',
            borderTop: '1px solid #f3f4f6'
          }}>
                    {s.display_name}
                </li>)}
            </ul>}
            {loading && <div className="hint" style={{
          marginTop: 6,
          fontSize: 12
        }}>Searching…</div>}
            {[street, city, stateProvince, postcode, country].some(Boolean) ? <div style={{
            marginTop: 8
          }}>
                <button type="button" onClick={() => {
              setSearchMode(false);
              setSearch('');
              setSuggestions([]);
              setOpen(false);
            }} style={{
              background: 'none',
              border: 'none',
              padding: 0,
              color: '#2563eb',
              cursor: 'pointer',
              textDecoration: 'underline',
              fontSize: 14
            }}>
                  Keep current address
                </button>
              </div> : null}
          </div> : [street, city, stateProvince, postcode, country].some(Boolean) ? <div style={{
          marginTop: -4,
          marginBottom: 8
        }}>
            <button type="button" onClick={() => {
            setSearchMode(true);
            setSearch('');
            setSuggestions([]);
            setOpen(false);
          }} style={{
            background: 'none',
            border: 'none',
            padding: 0,
            color: '#2563eb',
            cursor: 'pointer',
            textDecoration: 'underline',
            fontSize: 14
          }}>
              Change address
            </button>
          </div> : null}

        <TextInput id="editStreet" label="Street Address" value={street} onChange={e => {
            setStreet(e.target.value);
            setGeo(null);
          }} disabled={submitting} />
        <TextInput id="editCity" label="City" value={city} onChange={e => {
            setCity(e.target.value);
            setGeo(null);
          }} required disabled={submitting} />
        <TextInput id="editState" label="County/State" value={stateProvince} onChange={e => {
            setStateProvince(e.target.value);
            setGeo(null);
          }} disabled={submitting} />
        <TextInput id="editPostcode" label="Postcode" value={postcode} onChange={e => {
            setPostcode(e.target.value);
            setGeo(null);
          }} disabled={submitting} />
        <CountryPicker value={country} onChange={e => {
            setCountry(e.target.value);
            setGeo(null);
          }} required disabled={submitting} />

        {draftAddress.street || draftAddress.city || draftAddress.postcode || draftAddress.country ? <div style={{
          marginTop: 12
        }}>
            <label className="form-label">Map preview</label>
            <div style={{
            height: 220,
            borderRadius: 12,
            overflow: 'hidden',
            border: '1px solid rgba(15, 23, 42, 0.08)'
          }}>
              <TeamMap address={draftAddress} />
            </div>
          </div> : null}

        <div className="form-actions">
          <SubmitButton type="button" label="Cancel" onClick={onClose} disabled={submitting} variant="ghost" size="sm" />
          <SubmitButton type="submit" label={submitting ? 'Saving…' : 'Save'} disabled={submitting} variant="primary" />
        </div>
      </form>
    </>;
}
EditAddressForm.propTypes = {
  initialAddress: PropTypes.object,
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired
};
EditAddressForm.defaultProps = {
  initialAddress: {}
};

// ---------- hooks ----------
function useDebounce(value, delay) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setV(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return v;
}
